import { useEffect, useRef } from 'react';
import type { ComponentProps, Ref } from 'react';
import ModalFrame from '../primitives/ModalFrame';
import { impactOccurred, notificationOccurred } from '../../utils/telegram/hapticFeedback';

type ModalFrameProps = ComponentProps<typeof ModalFrame>;

export interface VibrationModalProps extends ModalFrameProps {
  /**
   * Set to false to disable the "strong vibration + shake" intro effect
   * (kept `true` by default so any modal using VibrationModal gets it for free).
   */
  vibrateOnOpen?: boolean;
}

const SHAKE_CLASS = 'vibration-modal-shake';
const SHAKE_DURATION_MS = 550;

/** Merge our own callback ref with whatever ref the caller passed in (object or callback). */
function mergeRefs<T>(refA: Ref<T> | undefined, refB: (node: T | null) => void) {
  return (node: T | null) => {
    refB(node);
    if (typeof refA === 'function') {
      refA(node);
    } else if (refA && typeof refA === 'object') {
      (refA as { current: T | null }).current = node;
    }
  };
}

/** Fires the strongest available device vibration: Telegram haptics + navigator.vibrate fallback. */
function fireStrongestVibration() {
  // Telegram WebApp haptics — "error" notification + "heavy" impact stacked for max intensity.
  try {
    notificationOccurred('error');
  } catch {
    // ignore — handled inside hapticFeedback utils too, this is just extra defense
  }
  try {
    impactOccurred('heavy');
  } catch {
    // ignore
  }

  // Plain-browser fallback (no-op inside Telegram WebApp, where vibrate is usually unavailable anyway).
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([90, 40, 90, 40, 140]);
    }
  } catch {
    // ignore
  }
}

/**
 * Reusable subclass/wrapper of ModalFrame: as soon as the modal has fully mounted/rendered
 * (isOpen transitions to true), it fires the strongest device vibration available and briefly
 * shakes the modal container via a CSS keyframe animation. Any other modal that needs this
 * "urgent/failure" feedback can simply render <VibrationModal ...> instead of <ModalFrame ...>.
 */
export default function VibrationModal({
  vibrateOnOpen = true,
  scrollContainerRef,
  isOpen,
  ...rest
}: VibrationModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !vibrateOnOpen) {
      hasFiredRef.current = false;
      return;
    }
    if (hasFiredRef.current) return;

    // Double rAF: wait until after the modal's first paint, so the shake is visible and
    // "on full mount/render" rather than firing before layout/paint settles.
    // NOTE: hasFiredRef is only flipped inside the rAF callback (once we actually fire), not
    // eagerly here — React StrictMode runs this effect mount -> cleanup -> mount once in dev,
    // and flipping the flag eagerly would make the (real) second mount think it already fired
    // when the first mount's rAFs were in fact cancelled by cleanup before they ran.
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (hasFiredRef.current) return;
        hasFiredRef.current = true;
        fireStrongestVibration();

        const node = containerRef.current;
        if (node) {
          node.classList.remove(SHAKE_CLASS);
          void node.offsetWidth; // force reflow so the animation restarts if re-triggered
          node.classList.add(SHAKE_CLASS);

          const clearShake = () => {
            node.classList.remove(SHAKE_CLASS);
            node.removeEventListener('animationend', clearShake);
          };
          node.addEventListener('animationend', clearShake);
          // Safety timeout in case animationend doesn't fire (e.g. node swapped out).
          setTimeout(clearShake, SHAKE_DURATION_MS + 100);
        }
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [isOpen, vibrateOnOpen]);

  // Callback ref (executed at commit, not during render) merged with the caller's ref.
  // eslint-disable-next-line react-hooks/refs -- mergeRefs callback assigns ref at commit, not render
  const setMergedScrollContainerRef = mergeRefs(scrollContainerRef, (node) => {
    containerRef.current = node;
  });

  return (
    <>
      <style>{`
        @keyframes vibrationModalShakeKeyframes {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
        .${SHAKE_CLASS} {
          animation: vibrationModalShakeKeyframes ${SHAKE_DURATION_MS}ms cubic-bezier(.36,.07,.19,.97) both;
        }
      `}</style>
      <ModalFrame
        {...rest}
        isOpen={isOpen}
        scrollContainerRef={setMergedScrollContainerRef}
      />
    </>
  );
}
